import React, { useState } from 'react'
import { Box, Typography, ButtonBase, Popover } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'

// Custom Golf Club Icon
const GolfClubIcon = ({ className, style }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={style}
  >
    <path
      d="M16.0206 29H8.54789C7.07703 28.9983 5.6669 28.413 4.62684 27.3726C3.58678 26.3322 3.00172 24.9216 3 23.4503L3 18.2464C3.00025 17.4205 3.18467 16.6052 3.53981 15.8597C3.89495 15.1142 4.41187 14.4573 5.05291 13.937C5.69396 13.4166 6.44297 13.0458 7.24538 12.8517C8.04779 12.6576 8.88338 12.645 9.69129 12.8148L24.6865 15.9686L26.8628 3L29 3.35885L26.193 20.11C26.0801 21.0467 25.8367 21.963 25.4701 22.8323C24.664 24.6673 23.3412 26.2278 21.6631 27.3231C19.985 28.4184 18.0243 29.0011 16.0206 29ZM8.54248 14.8595C7.64671 14.8609 6.78814 15.218 6.15534 15.8522C5.52255 16.4864 5.16728 17.3459 5.16757 18.242V23.4459C5.16728 24.3432 5.52319 25.2039 6.15706 25.8388C6.79094 26.4737 7.65089 26.8308 8.54789 26.8317H16.026C17.6074 26.8315 19.1546 26.3709 20.4788 25.5061C21.803 24.6414 22.847 23.4098 23.4835 21.9617C23.7716 21.265 23.9617 20.5316 24.0482 19.7826L24.3289 18.1087L9.24477 14.9321C9.01379 14.8838 8.77845 14.8595 8.54248 14.8595Z"
      fill="currentColor"
    />
    <path
      d="M8.60738 17.148L8.16064 19.2698L19.5697 21.6736L20.0164 19.5519L8.60738 17.148Z"
      fill="currentColor"
    />
    <path
      d="M7.56058 21.4348L7.10962 23.5557L14.6732 25.165L15.1242 23.0441L7.56058 21.4348Z"
      fill="currentColor"
    />
  </svg>
)

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
  },
  label: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  selectorButton: {
    width: '100%',
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `2px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(0, 48, 135, 0.02)',
    },
  },
  selectorButtonActive: {
    borderColor: theme.palette.primary.main,
  },
  selectorButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    '&:hover': {
      borderColor: theme.palette.grey[200],
      backgroundColor: theme.palette.background.paper,
    },
  },
  selectorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 10,
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainerSelected: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  clubIcon: {
    fontSize: 24,
  },
  textContainer: {
    textAlign: 'left',
  },
  placeholderText: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  selectedClubText: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  selectedClubSubtext: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  arrowIcon: {
    color: theme.palette.text.secondary,
    transition: 'transform 0.2s ease',
  },
  arrowIconOpen: {
    transform: 'rotate(180deg)',
  },

  // Popover styles
  popover: {
    marginTop: theme.spacing(1),
  },
  popoverPaper: {
    borderRadius: 16,
    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
    overflow: 'hidden',
    minWidth: 320,
    maxWidth: 400,
  },
  popoverContent: {
    padding: theme.spacing(2),
  },
  popoverTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
  },
  clubTypeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  clubTypeCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: theme.palette.primary.light,
      backgroundColor: 'rgba(0, 48, 135, 0.04)',
    },
  },
  clubTypeCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: 'rgba(0, 48, 135, 0.08)',
  },
  clubTypeLabel: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  numberSection: {
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    paddingTop: theme.spacing(2),
  },
  numberLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  numberGrid: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
  },
  numberChip: {
    minWidth: 44,
    height: 40,
    padding: theme.spacing(0, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(0, 48, 135, 0.04)',
    },
  },
  numberChipSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  numberChipLabel: {
    fontSize: '0.9375rem',
    fontWeight: 600,
  },
}))

const CLUB_TYPES = [
  { type: 'Driver', label: 'Driver', description: 'Tee shots' },
  {
    type: 'Wood',
    label: 'Wood',
    description: 'Fairway',
    options: ['3', '5', '7', '9'],
  },
  { type: 'Hybrid', label: 'Hybrid', description: 'Versatile' },
  {
    type: 'Iron',
    label: 'Iron',
    description: 'Approach',
    options: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
  },
  {
    type: 'Wedge',
    label: 'Wedge',
    description: 'Short game',
    options: ['PW', 'GW', 'SW', 'LW'],
  },
  { type: 'Other', label: 'Other', description: 'Custom' },
]

const getDisplayText = (type, number) => {
  if (!type) return null

  if (type === 'Wedge' && number) {
    const names = {
      PW: 'Pitching Wedge',
      GW: 'Gap Wedge',
      SW: 'Sand Wedge',
      LW: 'Lob Wedge',
    }
    return names[number] || number
  }

  if (number) {
    return `${number} ${type}`
  }

  return type
}

const getClubTypeValue = (type, number) => {
  if (!type) return ''

  if (type === 'Wedge' && number) {
    const names = {
      PW: 'Pitching Wedge',
      GW: 'Gap Wedge',
      SW: 'Sand Wedge',
      LW: 'Lob Wedge',
    }
    return names[number] || number
  }

  if (number) {
    return `${number} ${type}`
  }

  return type
}

const ClubSelector = ({ value, onChange, disabled }) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const parseValue = val => {
    if (!val) return { type: '', number: '' }

    const wedgeNames = {
      'Pitching Wedge': 'PW',
      'Gap Wedge': 'GW',
      'Sand Wedge': 'SW',
      'Lob Wedge': 'LW',
    }
    if (wedgeNames[val]) {
      return { type: 'Wedge', number: wedgeNames[val] }
    }

    const numberMatch = val.match(/^(\d+)\s+(.+)$/)
    if (numberMatch) {
      const [, number, type] = numberMatch
      return { type, number }
    }

    return { type: val, number: '' }
  }

  const { type: selectedType, number: selectedNumber } = parseValue(value)
  const selectedConfig = CLUB_TYPES.find(c => c.type === selectedType)
  const displayText = getDisplayText(selectedType, selectedNumber)

  const handleClick = event => {
    if (disabled) return
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleTypeSelect = clubType => {
    const config = CLUB_TYPES.find(c => c.type === clubType)

    if (config.options) {
      // Has sub-options, just select the type
      const value = getClubTypeValue(clubType, '')
      onChange(value)
    } else {
      // No sub-options, select and close
      const value = getClubTypeValue(clubType, '')
      onChange(value)
      handleClose()
    }
  }

  const handleNumberSelect = number => {
    const value = getClubTypeValue(selectedType, number)
    onChange(value)
    handleClose()
  }

  return (
    <Box className={classes.container}>
      <Typography className={classes.label}>Club Used</Typography>

      <ButtonBase
        className={`${classes.selectorButton} ${
          open ? classes.selectorButtonActive : ''
        } ${disabled ? classes.selectorButtonDisabled : ''}`}
        onClick={handleClick}
        disabled={disabled}
      >
        <Box className={classes.selectorContent}>
          <Box
            className={`${classes.iconContainer} ${
              selectedType ? classes.iconContainerSelected : ''
            }`}
          >
            <GolfClubIcon className={classes.clubIcon} />
          </Box>
          <Box className={classes.textContainer}>
            {displayText ? (
              <>
                <Typography className={classes.selectedClubText}>
                  {displayText}
                </Typography>
                {selectedConfig && (
                  <Typography className={classes.selectedClubSubtext}>
                    {selectedConfig.description}
                  </Typography>
                )}
              </>
            ) : (
              <Typography className={classes.placeholderText}>
                Select your club
              </Typography>
            )}
          </Box>
        </Box>
        <KeyboardArrowDownIcon
          className={`${classes.arrowIcon} ${
            open ? classes.arrowIconOpen : ''
          }`}
        />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className={classes.popover}
        PaperProps={{
          className: classes.popoverPaper,
        }}
      >
        <Box className={classes.popoverContent}>
          <Typography className={classes.popoverTitle}>
            What club did you use?
          </Typography>

          <Box className={classes.clubTypeGrid}>
            {CLUB_TYPES.map(club => (
              <ButtonBase
                key={club.type}
                className={`${classes.clubTypeCard} ${
                  selectedType === club.type ? classes.clubTypeCardSelected : ''
                }`}
                onClick={() => handleTypeSelect(club.type)}
              >
                <Typography className={classes.clubTypeLabel}>
                  {club.label}
                </Typography>
              </ButtonBase>
            ))}
          </Box>

          {selectedConfig?.options && (
            <Box className={classes.numberSection}>
              <Typography className={classes.numberLabel}>
                Which {selectedType.toLowerCase()}?
              </Typography>
              <Box className={classes.numberGrid}>
                {selectedConfig.options.map(num => (
                  <ButtonBase
                    key={num}
                    className={`${classes.numberChip} ${
                      selectedNumber === num ? classes.numberChipSelected : ''
                    }`}
                    onClick={() => handleNumberSelect(num)}
                  >
                    <Typography className={classes.numberChipLabel}>
                      {num}
                    </Typography>
                  </ButtonBase>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  )
}

export default ClubSelector
